import React, { Fragment } from "react";

import { Container } from "../../components";
import { lazyWithRetry } from "../../core/lazy_with_retry";
import { Spacer } from "../spacer";
import { GeneralInformation } from "../template_offering_details_base";
import { OfferingDetailsMultiPass } from "../template_offering_details_multipass";
import { Text } from "../text";
import { TextLink } from "../text_link";
import { useTheYardAnalytics } from "./theyard_analytics";
import { partnerConfig, testimonials } from "./theyard_partner_config";

const Theme = lazyWithRetry(() => import("./theyard_theme"));

const faqs = [
  {
    question: "What are your hours?",
    answer: "We are open from 9:30 AM to 5:30 PM, Monday through Friday.",
  },

  {
    question: "Can I share my multi passes with other people?",
    answer: "The passes are for personal use only and are not transferable.",
  },

  {
    question: "Can I use the meeting rooms?",
    answer:
      "Yes, the meeting rooms are charged at an hourly rate. " +
      "Please get in touch with the community manager for availability and pricing options.",
  },

  {
    question: "Can I use the printer?",
    answer:
      "Yes, please get in touch with the community manager and we will assist you.",
  },

  {
    question: "Can I meet with clients or bring a guest using my passes?",
    answer:
      "Your day pass is for personal use only and doesn’t cover guests. You may purchase additional day passes for each of your guests.",
  },

  {
    question: "Where can I keep and heat my lunch?",
    answer:
      "The kitchen, refrigerator and microwave is available for your use.",
  },

  {
    question: "Do you offer coffee or tea?",
    answer:
      "Modern kitchens include complimentary coffee by Eleva and tea by Harney & Sons.",
  },

  {
    question: "Do I need to book in advance to use my Multi-Pass?",
    answer: (
      <Text>
        Yes. You will need to book ahead of time. When possible, we will
        accommodate walk-ins according to availability.{" "}
        <TextLink href="https://partners.flexspace.ai/portal/theyard">
          Manage your bookings using the portal.
        </TextLink>
      </Text>
    ),
  },

  {
    question: "Can I cancel my day pass purchase?",
    answer: "The Yard Multi Pass is non-refundable.",
  },
  {
    question: "Does my multi-pass order expire?",
    answer:
      "You will have 30 days from purchase to use your 5 and 10 pass offerings. You will have 60 days from purchase to use the 30 pass offering.",
  },

  {
    question: "Can I change the date of my day pass booking?",
    answer: (
      <Text>
        Yes. you can{" "}
        <TextLink href="https://partners.flexspace.ai/portal/theyard">
          manage your bookings using the portal.
        </TextLink>
      </Text>
    ),
  },

  {
    question: "How do I login to the portal?",
    answer:
      "If you haven’t already done so, please click on the sign up and follow the prompts. " +
      "Be sure to use the same email address you used when purchasing your multi pass. " +
      "Once you have created your account, you can login to manage your bookings.",
  },
];

const generalInfo: GeneralInformation = {
  title: `General information`,
  description: "",
  bulletPoints: [
    {
      point: "Cancellations",
      text: "Order of the Multi-Day Pass is final and is not refundable.",
    },
    {
      point: "Expiration",
      text: "The 5 and 10 pass multi-pass offering expires 30 days from purchase. The 30 pass multi-pass offering expires 60 days from purchase.",
    },

    {
      point: "Access",
      text: "Access professional, beautifully designed workspaces and lounges at The Yard from 9:30 am to 5:30 pm, Monday - Friday.",
    },
    {
      point: "Complimentary beverages",
      text: "Unlimited free coffee, tea and water.",
    },
    {
      point: "Events",
      text: "Admitted attendance during the day’s events at The Yard.",
    },
    {
      point: "Art gallery",
      text: "Rotating art gallery installations accompanied by seasonal opening receptions supporting local artists.",
    },
  ],
};

export function OfferingTheYardMultiPass() {
  useTheYardAnalytics();

  return (
    <Fragment>
      <Theme />
      <OfferingDetailsMultiPass
        showAddress={false}
        showDirections
        partnerConfig={partnerConfig}
        data={{
          description: (
            <Container>
              <Text>
                The Yard multi-pass is perfect when you want to come in a couple
                of times a week. Purchase your day passes in bulk at a lower
                price without a long term commitment.
              </Text>
              <Spacer size={16} />
              <Text>
                Enjoy access to professional, beautifully designed workspaces
                and lounges at The Yard.
              </Text>
            </Container>
          ),
          faqs,
          testimonials: testimonials,
          generalInfo: generalInfo,
        }}
      />
    </Fragment>
  );
}
